import React, { useMemo, useState } from 'react';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Flame, Thermometer, Snowflake, Phone, ChevronRight,
  Sparkles, Trophy, XCircle, FileText, Inbox, Clock, Loader2,
  CheckSquare, Square, X, GripVertical, EyeOff, Eye,
} from 'lucide-react';
import { cn } from '../lib/utils';
import GradientAvatar from './GradientAvatar';

const STALE_DAYS = 7;
const STALE_MS = STALE_DAYS * 24 * 60 * 60 * 1000;
const isLeadStale = (lead) => {
  const ref = lead.updated_at || lead.last_contacted_at || lead.created_at;
  if (!ref) return false;
  const t = Date.parse(ref);
  if (Number.isNaN(t)) return false;
  return Date.now() - t > STALE_MS;
};

// Status ordering for pipeline visualisation — pre-qualification CRM stages.
// `tone` is used to drive the column header gradient + subtle tinted background.
// `group` distinguishes progression stages from final outcomes (converted/lost).
export const STATUS_ORDER = [
  { key: 'new_lead',           label: 'New Lead',          tone: 'purple',  color: 'from-purple-500 to-fuchsia-500',  dot: 'bg-purple-500',  icon: Inbox,    group: 'flow' },
  { key: 'contacted',          label: 'Contacted',         tone: 'blue',    color: 'from-blue-500 to-cyan-500',       dot: 'bg-blue-500',    icon: Phone,    group: 'flow' },
  { key: 'discovery',          label: 'Discovery',         tone: 'cyan',    color: 'from-cyan-500 to-teal-500',       dot: 'bg-cyan-500',    icon: Clock,    group: 'flow' },
  { key: 'educating',          label: 'Educating',         tone: 'teal',    color: 'from-teal-500 to-emerald-500',    dot: 'bg-teal-500',    icon: FileText, group: 'flow' },
  { key: 'pre_qualifying',     label: 'Pre-Qualifying',    tone: 'amber',   color: 'from-amber-500 to-orange-500',    dot: 'bg-amber-500',   icon: Loader2,  group: 'flow' },
  { key: 'ready_to_proceed',   label: 'Ready to Proceed',  tone: 'orange',  color: 'from-orange-500 to-rose-500',     dot: 'bg-orange-500',  icon: Sparkles, group: 'flow' },
  { key: 'long_term_nurture',  label: 'Long-term Nurture', tone: 'indigo',  color: 'from-indigo-500 to-violet-500',   dot: 'bg-indigo-500',  icon: Snowflake,group: 'parking' },
  { key: 'converted',          label: 'Converted',         tone: 'emerald', color: 'from-emerald-500 to-green-500',   dot: 'bg-emerald-500', icon: Trophy,   group: 'outcome' },
  { key: 'lost',               label: 'Lost',              tone: 'slate',   color: 'from-slate-500 to-slate-600',     dot: 'bg-slate-500',   icon: XCircle,  group: 'outcome' },
];

// Per-tone CSS palette — drives column tint + count chip
const TONE_CLASSES = {
  purple:  { tintBg: 'bg-purple-50/60 dark:bg-purple-950/20',  border: 'border-purple-200/70 dark:border-purple-800/40',  chip: 'bg-purple-100 text-purple-700 dark:bg-purple-500/15 dark:text-purple-300' },
  blue:    { tintBg: 'bg-blue-50/60 dark:bg-blue-950/20',      border: 'border-blue-200/70 dark:border-blue-800/40',      chip: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300' },
  cyan:    { tintBg: 'bg-cyan-50/60 dark:bg-cyan-950/20',      border: 'border-cyan-200/70 dark:border-cyan-800/40',      chip: 'bg-cyan-100 text-cyan-700 dark:bg-cyan-500/15 dark:text-cyan-300' },
  teal:    { tintBg: 'bg-teal-50/60 dark:bg-teal-950/20',      border: 'border-teal-200/70 dark:border-teal-800/40',      chip: 'bg-teal-100 text-teal-700 dark:bg-teal-500/15 dark:text-teal-300' },
  amber:   { tintBg: 'bg-amber-50/60 dark:bg-amber-950/20',    border: 'border-amber-200/70 dark:border-amber-800/40',    chip: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300' },
  orange:  { tintBg: 'bg-orange-50/60 dark:bg-orange-950/20',  border: 'border-orange-200/70 dark:border-orange-800/40',  chip: 'bg-orange-100 text-orange-700 dark:bg-orange-500/15 dark:text-orange-300' },
  indigo:  { tintBg: 'bg-indigo-50/60 dark:bg-indigo-950/20',  border: 'border-indigo-200/70 dark:border-indigo-800/40',  chip: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-500/15 dark:text-indigo-300' },
  emerald: { tintBg: 'bg-emerald-50/60 dark:bg-emerald-950/20',border: 'border-emerald-200/70 dark:border-emerald-800/40',chip: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-300' },
  slate:   { tintBg: 'bg-slate-50/60 dark:bg-slate-900/50',    border: 'border-slate-200/70 dark:border-slate-700/60',    chip: 'bg-slate-100 text-slate-700 dark:bg-slate-700/50 dark:text-slate-300' },
};

const PRIORITY_ICON = { Hot: Flame, Warm: Thermometer, Cold: Snowflake };
const PRIORITY_COLOR = {
  Hot: 'text-rose-500 dark:text-rose-400',
  Warm: 'text-amber-500 dark:text-amber-400',
  Cold: 'text-blue-500 dark:text-blue-400',
};
const PRIORITY_EDGE = {
  Hot: 'bg-gradient-to-b from-rose-500 to-orange-500',
  Warm: 'bg-gradient-to-b from-amber-400 to-amber-500',
  Cold: 'bg-gradient-to-b from-blue-400 to-cyan-500',
};
const PRIORITY_PILL = {
  Hot: 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300',
  Warm: 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300',
  Cold: 'bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-300',
};

const LeadCardCompact = ({ lead, onSelect, onToggleSelect, isSelected, selectionMode, onDragStart, onDragEnd, isDragging }) => {
  const Icon = PRIORITY_ICON[lead.priority] || Thermometer;
  const edge = PRIORITY_EDGE[lead.priority] || 'bg-slate-300 dark:bg-slate-600';
  const pill = PRIORITY_PILL[lead.priority] || 'bg-slate-100 text-slate-700 dark:bg-slate-700/50 dark:text-slate-300';

  const handleClick = (e) => {
    if (selectionMode) {
      e.stopPropagation();
      onToggleSelect(lead.id);
    } else {
      onSelect(lead);
    }
  };

  return (
    <div
      draggable={!selectionMode}
      onDragStart={(e) => onDragStart && onDragStart(e, lead)}
      onDragEnd={onDragEnd}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleClick(e); }}}
      className={cn(
        'relative w-full text-left rounded-lg bg-white dark:bg-slate-800/80 border transition-all group cursor-pointer overflow-hidden',
        'hover:border-emerald-400 dark:hover:border-emerald-500 hover:shadow-md dark:hover:shadow-emerald-500/10 hover:-translate-y-0.5',
        isSelected
          ? 'border-emerald-500 dark:border-emerald-400 ring-2 ring-emerald-500/30'
          : 'border-slate-200 dark:border-slate-700/80',
        isDragging && 'opacity-40 scale-95 rotate-1',
        !selectionMode && 'select-none'
      )}
      data-testid={`pipeline-lead-${lead.id}`}
    >
      {/* Priority edge strip */}
      <div className={cn('absolute left-0 top-0 bottom-0 w-1', edge)} aria-hidden="true" />

      <div className="pl-3 pr-2.5 py-2.5">
        {/* Selection checkbox (overlay; only visible on hover unless selected) */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onToggleSelect(lead.id); }}
          className={cn(
            'absolute top-1.5 right-1.5 w-5 h-5 flex items-center justify-center rounded transition-opacity',
            isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
          )}
          aria-label={isSelected ? 'Deselect lead' : 'Select lead'}
          data-testid={`pipeline-lead-select-${lead.id}`}
        >
          {isSelected ? (
            <CheckSquare className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
          ) : (
            <Square className="w-4 h-4 text-slate-400 dark:text-slate-500" />
          )}
        </button>

        {/* Top row: avatar + name + priority pill */}
        <div className="flex items-start gap-2 pr-6">
          <GradientAvatar
            name={lead.name}
            role="Lead"
            keyId={lead.id || lead.name}
            size="xs"
            className="shrink-0 w-7 h-7 text-[10px]"
            subtitle={lead.lead_type || lead.status?.replace(/_/g, ' ')}
          />
          <p className="text-sm font-semibold text-slate-900 dark:text-white truncate leading-tight flex-1 min-w-0">{lead.name}</p>
        </div>

        {/* Meta row: type + source */}
        <p className="text-[11px] text-slate-500 dark:text-slate-400 truncate mt-1 pl-9">
          {lead.lead_type || '—'}{lead.lead_source ? <span className="opacity-50"> · {lead.lead_source}</span> : null}
        </p>

        {/* Footer chips: temperature + attempt counter + hover arrow */}
        <div className="flex items-center justify-between gap-1.5 mt-2 pl-9">
          <span className={cn('inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium uppercase tracking-wide', pill)}>
            <Icon className="w-2.5 h-2.5" />
            {lead.priority || 'Warm'}
          </span>
          <div className="flex items-center gap-1">
            {(lead.no_answer_attempts || 0) > 0 && lead.status === 'new_lead' && (
              <span
                className={cn(
                  'inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-medium tabular-nums',
                  (lead.no_answer_attempts || 0) >= 2
                    ? 'bg-rose-100 text-rose-700 dark:bg-rose-500/15 dark:text-rose-300'
                    : 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300'
                )}
                title={`${lead.no_answer_attempts}/3 no-contact attempts — auto-parks at 3`}
              >
                {lead.no_answer_attempts}/3
              </span>
            )}
            <ChevronRight className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all text-crm-blue" />
          </div>
        </div>
      </div>

      {/* drag handle hint */}
      {!selectionMode && (
        <span className="absolute bottom-1 right-1.5 opacity-0 group-hover:opacity-40 transition-opacity">
          <GripVertical className="w-3 h-3 text-slate-400" />
        </span>
      )}
    </div>
  );
};

const StatusColumn = ({
  status, leads, onSelect, expanded, onToggle,
  onToggleSelect, selectedIds, selectionMode,
  onDragStart, onDragEnd, draggingId, onDropOnStatus, isDropTarget, onDragOver, onDragLeave,
  hideStale, columnRevealed, onToggleReveal,
}) => {
  const Icon = status.icon;
  const total = leads.length;
  const tone = TONE_CLASSES[status.tone] || TONE_CLASSES.slate;

  // Stale partition — only applied to active (non-outcome) progression stages
  const isStaleFilterable = status.group === 'flow' && hideStale && !columnRevealed;
  const visibleLeads = isStaleFilterable ? leads.filter(l => !isLeadStale(l)) : leads;
  const hiddenStaleCount = isStaleFilterable ? leads.length - visibleLeads.length : 0;

  return (
    <div
      className={cn(
        'flex flex-col flex-shrink-0 w-72 rounded-2xl border transition-all overflow-hidden',
        tone.tintBg,
        tone.border,
        expanded && 'ring-2 ring-emerald-500/40 shadow-lg',
        isDropTarget && 'ring-2 ring-emerald-500 scale-[1.01] shadow-emerald-500/20 shadow-lg'
      )}
      data-testid={`pipeline-column-${status.key}`}
      onDragOver={(e) => onDragOver(e, status.key)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDropOnStatus(e, status.key)}
    >
      {/* Vivid gradient top bar — gives each stage a unique brand-coloured anchor */}
      <div className={cn('h-1 w-full bg-gradient-to-r', status.color)} aria-hidden="true" />

      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-2 px-3 py-2.5 hover:bg-white/40 dark:hover:bg-slate-800/30 transition text-left group/header"
        title={`Toggle ${status.label}`}
      >
        <span className={cn('flex items-center justify-center w-7 h-7 rounded-lg bg-gradient-to-br shadow-sm', status.color)}>
          <Icon className="w-3.5 h-3.5 text-white" />
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[13px] font-semibold text-slate-900 dark:text-white truncate leading-tight">{status.label}</span>
          {status.group === 'outcome' && (
            <span className="block text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider">Outcome</span>
          )}
          {status.group === 'parking' && (
            <span className="block text-[10px] text-slate-500 dark:text-slate-400 uppercase tracking-wider">Long-term</span>
          )}
        </span>
        <span className={cn('inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 rounded-md text-xs font-bold tabular-nums', tone.chip)}>
          {total}
        </span>
      </button>

      <div className="px-2 pb-2 space-y-1.5 overflow-y-auto max-h-[62vh] min-h-[140px]">
        {visibleLeads.length === 0 && hiddenStaleCount === 0 ? (
          <div className={cn(
            'flex flex-col items-center justify-center py-8 rounded-lg border-2 border-dashed transition-colors',
            isDropTarget
              ? 'border-emerald-500/60 text-emerald-600 dark:text-emerald-400 bg-emerald-500/5'
              : 'border-slate-300/60 dark:border-slate-700/40 text-slate-400 dark:text-slate-600'
          )}>
            <Icon className="w-5 h-5 mb-1 opacity-50" />
            <p className="text-[11px] font-medium">{isDropTarget ? 'Drop to move here' : 'No leads'}</p>
          </div>
        ) : (
          visibleLeads.slice(0, expanded ? 100 : 6).map(lead => (
            <LeadCardCompact
              key={lead.id}
              lead={lead}
              onSelect={onSelect}
              onToggleSelect={onToggleSelect}
              isSelected={selectedIds.has(lead.id)}
              selectionMode={selectionMode}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              isDragging={draggingId === lead.id}
            />
          ))
        )}
        {!expanded && visibleLeads.length > 6 && (
          <button
            type="button"
            onClick={onToggle}
            className="w-full py-2 text-[11px] font-semibold text-emerald-600 dark:text-emerald-400 hover:text-emerald-700 dark:hover:text-emerald-300 hover:bg-emerald-500/5 rounded-md transition-colors"
          >
            +{visibleLeads.length - 6} more →
          </button>
        )}
        {/* Stale-reveal chip — only shown when there are stale leads being hidden in this column */}
        {hiddenStaleCount > 0 && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleReveal(status.key); }}
            className="w-full mt-1 py-1.5 px-2 text-[11px] font-medium text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-200 bg-slate-100/70 dark:bg-slate-800/40 hover:bg-slate-200/80 dark:hover:bg-slate-700/60 rounded-md border border-dashed border-slate-300/60 dark:border-slate-700/40 transition-colors flex items-center justify-center gap-1.5"
            data-testid={`pipeline-reveal-stale-${status.key}`}
            title={`${hiddenStaleCount} stale lead${hiddenStaleCount === 1 ? '' : 's'} (no activity > ${STALE_DAYS} days)`}
          >
            <Clock className="w-3 h-3" />
            +{hiddenStaleCount} stale · show
          </button>
        )}
        {columnRevealed && hiddenStaleCount === 0 && leads.some(isLeadStale) && (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); onToggleReveal(status.key); }}
            className="w-full mt-1 py-1.5 px-2 text-[11px] font-medium text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200 transition-colors"
          >
            Hide stale
          </button>
        )}
      </div>
    </div>
  );
};

/**
 * LeadPipelineView
 * Kanban board on desktop with drag-and-drop status changes + multi-select bulk operations.
 *
 * Props:
 *  - leads: full lead list
 *  - onSelectLead: callback when a lead card is clicked (opens detail panel)
 *  - onMoveLead(leadId, newStatus): called when a lead is dragged to a new column
 *  - onBulkUpdateStatus(leadIds, newStatus): called from the bulk action bar
 *  - onBulkDelete(leadIds): called from the bulk action bar
 */
export default function LeadPipelineView({ leads = [], onSelectLead, onMoveLead, onBulkUpdateStatus, onBulkDelete }) {
  const [expandedStatus, setExpandedStatus] = useState(null);
  const [activePill, setActivePill] = useState('all');
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [draggingId, setDraggingId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [hideStale, setHideStale] = useState(true);    // default: hide stale leads in the kanban
  const [revealedColumns, setRevealedColumns] = useState(new Set()); // per-column "show stale" overrides

  const grouped = useMemo(() => {
    const map = {};
    STATUS_ORDER.forEach(s => { map[s.key] = []; });
    leads.forEach(l => {
      if (map[l.status]) map[l.status].push(l);
      else map.new_lead.push(l);
    });
    return map;
  }, [leads]);

  const toggleRevealColumn = (key) => {
    setRevealedColumns(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key); else next.add(key);
      return next;
    });
  };

  const totalLeads = leads.length;
  const hotLeads = leads.filter(l => l.priority === 'Hot').length;
  const inFlight = leads.filter(l => !['closed_won', 'closed_lost'].includes(l.status)).length;

  const mobileLeads = activePill === 'all' ? leads : grouped[activePill] || [];

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const selectionMode = selectedIds.size > 0;

  // Drag handlers
  const handleDragStart = (e, lead) => {
    setDraggingId(lead.id);
    e.dataTransfer.setData('text/plain', lead.id);
    e.dataTransfer.effectAllowed = 'move';
  };
  const handleDragEnd = () => { setDraggingId(null); setDropTarget(null); };
  const handleDragOver = (e, statusKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dropTarget !== statusKey) setDropTarget(statusKey);
  };
  const handleDragLeave = () => setDropTarget(null);
  const handleDrop = (e, statusKey) => {
    e.preventDefault();
    const id = e.dataTransfer.getData('text/plain') || draggingId;
    setDropTarget(null);
    setDraggingId(null);
    if (!id) return;
    const lead = leads.find(l => l.id === id);
    if (!lead || lead.status === statusKey) return;
    if (onMoveLead) onMoveLead(id, statusKey);
  };

  return (
    <div className="space-y-4" data-testid="lead-pipeline-view">
      {/* Pipeline summary strip */}
      <div className="grid grid-cols-3 gap-3">
        <div className="rounded-xl p-3 sm:p-4 bg-white dark:bg-slate-900/70 border border-slate-200 dark:border-slate-800">
          <p className="text-xs text-slate-500 dark:text-slate-400">Total Leads</p>
          <p className="text-2xl sm:text-3xl font-bold text-slate-900 dark:text-white">{totalLeads}</p>
        </div>
        <div className="rounded-xl p-3 sm:p-4 bg-gradient-to-br from-rose-500/10 to-orange-500/10 border border-rose-500/30 dark:border-rose-500/40">
          <p className="text-xs text-rose-600 dark:text-rose-300 flex items-center gap-1">
            <Flame className="w-3 h-3" /> Hot
          </p>
          <p className="text-2xl sm:text-3xl font-bold text-rose-600 dark:text-rose-300">{hotLeads}</p>
        </div>
        <div className="rounded-xl p-3 sm:p-4 bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/30 dark:border-emerald-500/40">
          <p className="text-xs text-emerald-600 dark:text-emerald-300">In Flight</p>
          <p className="text-2xl sm:text-3xl font-bold text-emerald-600 dark:text-emerald-300">{inFlight}</p>
        </div>
      </div>

      {/* Stale toggle row */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400 font-medium">
          {hideStale ? `Showing leads active in the last ${STALE_DAYS} days` : 'Showing all leads (including stale)'}
        </p>
        <button
          type="button"
          onClick={() => setHideStale((v) => !v)}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition-colors',
            hideStale
              ? 'bg-cyan-50 dark:bg-cyan-500/10 text-cyan-700 dark:text-cyan-300 border-cyan-300/60 dark:border-cyan-500/30 hover:bg-cyan-100 dark:hover:bg-cyan-500/20'
              : 'bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-200 border-slate-300/60 dark:border-slate-700 hover:bg-slate-200 dark:hover:bg-slate-700'
          )}
          data-testid="pipeline-stale-toggle"
          title={hideStale ? 'Reveal stale leads (no activity > 7 days)' : 'Hide stale leads (no activity > 7 days)'}
        >
          {hideStale ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
          {hideStale ? 'Stale hidden' : 'Show stale'}
        </button>
      </div>

      {/* Bulk action bar */}
      {selectionMode && (
        <div
          className="sticky top-0 z-20 flex flex-wrap items-center gap-2 p-3 rounded-xl bg-emerald-600 text-white shadow-lg dark:bg-emerald-700"
          data-testid="bulk-action-bar"
        >
          <span className="font-semibold text-sm" data-testid="bulk-selection-count">
            {selectedIds.size} selected
          </span>
          <span className="hidden sm:inline text-xs opacity-80">— bulk actions:</span>
          <select
            className="ml-auto sm:ml-0 px-2 py-1 rounded text-xs text-slate-900 dark:text-slate-100 dark:bg-slate-800"
            data-testid="bulk-status-select"
            defaultValue=""
            onChange={(e) => {
              const v = e.target.value;
              if (!v) return;
              if (onBulkUpdateStatus) onBulkUpdateStatus([...selectedIds], v);
              e.target.value = '';
              clearSelection();
            }}
          >
            <option value="">Move to status…</option>
            {STATUS_ORDER.map(s => (
              <option key={s.key} value={s.key}>{s.label}</option>
            ))}
          </select>
          <Button
            type="button"
            size="sm"
            variant="destructive"
            onClick={() => {
              if (window.confirm(`Delete ${selectedIds.size} lead(s)? This cannot be undone.`)) {
                if (onBulkDelete) onBulkDelete([...selectedIds]);
                clearSelection();
              }
            }}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="bulk-delete-btn"
          >
            Delete
          </Button>
          <Button type="button" size="sm" variant="ghost" onClick={clearSelection} className="text-white hover:bg-white/10" data-testid="bulk-clear-btn">
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
        </div>
      )}

      {/* Mobile: status pills */}
      <div className="md:hidden">
        <div className="flex gap-2 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
          <button
            onClick={() => setActivePill('all')}
            className={cn(
              'flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition',
              activePill === 'all'
                ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white'
                : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700'
            )}
          >
            All <Badge variant="secondary" className="bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 ml-1 px-1.5">{totalLeads}</Badge>
          </button>
          {STATUS_ORDER.map(s => (
            <button
              key={s.key}
              onClick={() => setActivePill(s.key)}
              className={cn(
                'flex-shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium border transition',
                activePill === s.key
                  ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 border-slate-900 dark:border-white'
                  : 'bg-white dark:bg-slate-800 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700'
              )}
              data-testid={`pipeline-pill-${s.key}`}
            >
              <span className={cn('w-1.5 h-1.5 rounded-full', s.dot)}></span>
              {s.label}
              <Badge variant="secondary" className="bg-slate-200 dark:bg-slate-700 text-slate-700 dark:text-slate-200 ml-0.5 px-1.5">
                {grouped[s.key]?.length || 0}
              </Badge>
            </button>
          ))}
        </div>

        <div className="mt-3 space-y-2">
          {mobileLeads.length === 0 ? (
            <div className="text-center py-12 text-slate-400 dark:text-slate-600">
              <Inbox className="w-8 h-8 mx-auto mb-2" />
              <p className="text-sm">No leads in this stage</p>
            </div>
          ) : (
            mobileLeads.map(lead => (
              <LeadCardCompact
                key={lead.id}
                lead={lead}
                onSelect={onSelectLead}
                onToggleSelect={toggleSelect}
                isSelected={selectedIds.has(lead.id)}
                selectionMode={selectionMode}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                isDragging={draggingId === lead.id}
              />
            ))
          )}
        </div>
      </div>

      {/* Desktop: kanban board with progression flow + outcomes rail */}
      <div className="hidden md:block">
        <div className="flex items-stretch gap-3 overflow-x-auto pb-4 scrollbar-thin">
          {STATUS_ORDER.map((status, idx) => {
            const prev = STATUS_ORDER[idx - 1];
            const showOutcomeDivider = prev && prev.group !== 'outcome' && status.group === 'outcome';
            return (
              <React.Fragment key={status.key}>
                {/* Vertical divider before the Outcomes rail */}
                {showOutcomeDivider && (
                  <div className="flex flex-col items-center justify-center px-2" aria-hidden="true">
                    <div className="w-px flex-1 bg-gradient-to-b from-transparent via-slate-300 dark:via-slate-700 to-transparent" />
                    <span className="my-3 text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-400 dark:text-slate-500 [writing-mode:vertical-rl] rotate-180">Outcomes</span>
                    <div className="w-px flex-1 bg-gradient-to-b from-transparent via-slate-300 dark:via-slate-700 to-transparent" />
                  </div>
                )}
                <StatusColumn
                  status={status}
                  leads={grouped[status.key] || []}
                  onSelect={onSelectLead}
                  expanded={expandedStatus === status.key}
                  onToggle={() => setExpandedStatus(prev => prev === status.key ? null : status.key)}
                  onToggleSelect={toggleSelect}
                  selectedIds={selectedIds}
                  selectionMode={selectionMode}
                  onDragStart={handleDragStart}
                  onDragEnd={handleDragEnd}
                  draggingId={draggingId}
                  isDropTarget={dropTarget === status.key}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDropOnStatus={handleDrop}
                  hideStale={hideStale}
                  columnRevealed={revealedColumns.has(status.key)}
                  onToggleReveal={toggleRevealColumn}
                />
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}
